import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { formatCurrency } from '../../../services/campaignService';

const TimelineLaunch = ({ data, updateData, errors }) => {
  const [newHashtag, setNewHashtag] = useState('');

  const addHashtag = () => {
    if (newHashtag?.trim()) {
      const hashtag = newHashtag?.trim()?.startsWith('#') ? newHashtag?.trim() : `#${newHashtag?.trim()}`;
      const currentHashtags = data?.hashtags || [];
      if (!currentHashtags?.includes(hashtag)) {
        updateData('hashtags', [...currentHashtags, hashtag]);
      }
      setNewHashtag('');
    }
  };

  const removeHashtag = (hashtag) => {
    const currentHashtags = data?.hashtags || [];
    updateData('hashtags', currentHashtags?.filter(h => h !== hashtag));
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      addHashtag();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Timeline & Launch</h2>
        <p className="text-muted-foreground">Set your campaign timeline and finalize details</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Start Date"
            type="date"
            value={data?.startDate || ''}
            onChange={(e) => updateData('startDate', e?.target?.value)}
            error={errors?.startDate}
            required
          />
          <Input
            label="End Date"
            type="date"
            value={data?.endDate || ''}
            onChange={(e) => updateData('endDate', e?.target?.value)}
            error={errors?.endDate}
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Campaign Hashtags
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="Enter hashtag (e.g., MusicPromo2026)"
              value={newHashtag}
              onChange={(e) => setNewHashtag(e?.target?.value)}
              onKeyPress={handleKeyPress}
            />
            <Button
              onClick={addHashtag}
              iconName="Plus"
              variant="outline"
            >
              Add
            </Button>
          </div>
          {data?.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {data?.hashtags?.map((hashtag, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium"
                >
                  <span>{hashtag}</span>
                  <button
                    type="button"
                    onClick={() => removeHashtag(hashtag)}
                    className="hover:text-primary/70"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Campaign Summary */}
        <div className="bg-muted rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
            <Icon name="FileText" size={20} />
            Campaign Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Campaign Name:</span>
              <span className="font-medium text-foreground">{data?.name || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Genre:</span>
              <span className="font-medium text-foreground capitalize">{data?.genre || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Budget:</span>
              <span className="font-medium text-foreground">
                {data?.budget ? formatCurrency(parseFloat(data?.budget)) : 'Not set'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Content Types:</span>
              <span className="font-medium text-foreground">
                {data?.contentTypes?.length > 0 ? data?.contentTypes?.length : 'None selected'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Selected Influencers:</span>
              <span className="font-medium text-foreground">
                {data?.selectedInfluencers?.length > 0 ? data?.selectedInfluencers?.length : 'None selected'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration:</span>
              <span className="font-medium text-foreground">
                {data?.startDate && data?.endDate
                  ? `${Math.ceil((new Date(data?.endDate) - new Date(data?.startDate)) / (1000 * 60 * 60 * 24))} days`
                  : 'Not set'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
          <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <p className="font-medium mb-1">Ready to Launch</p>
            <p className="text-green-800">
              Review all details above. Once launched, your campaign will be visible to selected influencers and they can start submitting proposals.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineLaunch;
